import { CustomerDomain, useUpdateDomainAddressMutation } from "@/redux/reducer/customers.api";
import { getUserTenantId } from "@talentech/bff-login";
import { ModalForm, TalentechFormFieldTypes, Typography } from "@talentech/components";
import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";
import { HOSTED_ALIAS_PATTERN, addressChanges, rejectionMessage, hostedAddress } from "./domainAddress";

const validationSchema = yup.object().shape({
  address: yup.object().shape({
    domain_name: yup.string().trim().required("This field is required"),
    customer_alias: yup
      .string()
      .required("This field is required")
      .matches(HOSTED_ALIAS_PATTERN, "This field must contain only lowercase letters and numbers"),
  }),
});

type AddressForm = { address: { domain_name: string; customer_alias: string } };

const EditDomainAddressModal: React.FC<{
  domain: CustomerDomain;
  onClose: () => void;
}> = ({ domain, onClose }) => {
  const tenantId = useSelector(getUserTenantId);
  const [alias, setAlias] = useState(domain.customer_alias || "");
  const [updateDomainAddress] = useUpdateDomainAddressMutation();

  const onSubmit = ({ address }: AddressForm) => {
    const changes = addressChanges(domain, address);
    if (!Object.keys(changes).length) {
      onClose();
      return;
    }
    const toastId = toast.loading("Changing the address...");
    updateDomainAddress({ tenant_id: tenantId, domain_id: domain.domain_id, ...changes })
      .unwrap()
      .then(() => {
        toast.update(toastId, { render: "Address changed", type: "success", isLoading: false, autoClose: 5000 });
        onClose();
      })
      .catch((error) => {
        toast.update(toastId, {
          render: rejectionMessage(error, "Failed to change the address"),
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      });
  };

  const fields = [
    {
      type: TalentechFormFieldTypes.TEXT,
      label: "Name",
      field: "domain_name",
      size: { xs: 12 },
      initialValue: domain.domain_name,
    },
    {
      type: TalentechFormFieldTypes.TEXT,
      label: "Alias",
      field: "customer_alias",
      size: { xs: 12 },
      initialValue: domain.customer_alias || "",
      callback: (value: string) => setAlias(value),
      additionalContent: (
        <Typography variant="p" className="small text-muted mb-0">
          New address: {hostedAddress(domain.domain_url, alias)}. The old address stops working, and the pages are
          updated to the new one.
        </Typography>
      ),
    },
  ];

  return (
    <ModalForm
      modal={{ showModal: true, title: "Edit address", toggleModal: onClose }}
      form={{
        validationSchema,
        handleSubmit: onSubmit,
        formFields: { fields: fields as any, id: "address", section: "" },
      }}
    />
  );
};

export default EditDomainAddressModal;
